// booking.js
import { formatCurrency, formatDate } from '../utils/helpers.js';
import { isValidEmail, isValidPhone, formatPhoneForStorage } from '../utils/validation.js';
import { submitBookingForm } from '../api/form-submission.js';
import { showSuccessNotification, showErrorNotification, showMockWarningNotification, clearFormFields } from '../utils/notifications.js';

document.addEventListener('DOMContentLoaded', function() {
    // Only run booking page logic if vehiclesContainer exists
    const vehiclesContainer = document.getElementById('vehiclesContainer');
    const loadMoreBtn = document.getElementById('loadMoreBtn');
    const bookingForm = document.getElementById('bookingForm');
    const successModal = document.getElementById('successModal');
    const loadingOverlay = document.getElementById('loadingOverlay');
    const newBookingSection = document.getElementById('newBookingSection');
    if (!vehiclesContainer || !loadMoreBtn || !bookingForm || !successModal || !loadingOverlay || !newBookingSection) return;

    let vehicleData = [];

    // Global variables
    let currentVehicleIndex = 0;
    let vehiclesPerLoad = 3;
    let selectedVehicle = null;
    let isLoading = false;

    // DOM elements
    const successModalContent = document.getElementById('successModalContent');

    // Initialize the page
    function init() {
        fetch('/data/vehicles.json')
            .then(res => res.json())
            .then(data => {
                vehicleData = data;
                loadInitialVehicles();
                populateLocationDropdowns();
            });
        setupEventListeners();
        setMinDate();
    }

    // Load initial vehicles
    function loadInitialVehicles() {
        showLoading();
        setTimeout(() => {
            loadVehicles();
            hideLoading();
        }, 1000);
    }

    // Load vehicles function
    function loadVehicles() {
        const vehiclesToLoad = vehicleData.slice(currentVehicleIndex, currentVehicleIndex + vehiclesPerLoad);
        
        vehiclesToLoad.forEach((vehicle, index) => {
            setTimeout(() => {
                const vehicleCard = createVehicleCard(vehicle);
                vehiclesContainer.appendChild(vehicleCard);
                vehicleCard.classList.add('fade-in');
            }, index * 200);
        });

        currentVehicleIndex += vehiclesPerLoad;

        // Hide load more button if all vehicles are loaded
        if (currentVehicleIndex >= vehicleData.length) {
            loadMoreBtn.style.display = 'none';
        }
    }

    // Create vehicle card HTML
    // Determine the appropriate icon based on vehicle type
    function getVehicleIcon(type) {
        switch(type.toLowerCase()) {
            case 'sedan':
            case 'luxury sedan':
                return 'fas fa-car';
            case 'suv':
                return 'fas fa-car-side';
            case 'van':
                return 'fas fa-shuttle-van';
            case 'bus':
                return 'fas fa-bus';
            default:
                return 'fas fa-car';
        }
    }

    function createVehicleCard(vehicle) {
        const card = document.createElement('div');
        card.className = 'vehicle-card';
        
        card.innerHTML = `
            <div class="vehicle-image">
                <img src="${vehicle.image}" alt="${vehicle.name}" 
                     onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
                <div class="vehicle-icon-fallback" style="display: none;">
                    <i class="${getVehicleIcon(vehicle.type)}"></i>
                </div>
                <div class="vehicle-badge">${vehicle.badge}</div>
            </div>
            <div class="vehicle-info">
                <div class="vehicle-header">
                    <div>
                        <h3 class="vehicle-name">${vehicle.name}</h3>
                        <div class="vehicle-type">${vehicle.type}</div>
                        <div class="vehicle-rating">
                            <div class="stars">
                                ${generateStars(vehicle.rating)}
                            </div>
                            <span class="rating-value">${vehicle.rating}</span>
                        </div>
                    </div>
                </div>
                
                <div class="vehicle-features">
                    ${vehicle.features.map(feature => `
                        <div class="feature-item">
                            <i class="fas fa-check"></i>
                            <span>${feature}</span>
                        </div>
                    `).join('')}
                </div>
                
                <p class="vehicle-description">${vehicle.description}</p>
                
                <div class="vehicle-actions">
                    <button class="btn-book" onclick="selectVehicle(${vehicle.id})">
                        <i class="fas fa-calendar-check"></i>
                        Book Now
                    </button>
                    <button class="btn-details" onclick="showVehicleDetails(${vehicle.id})">
                        <i class="fas fa-info-circle"></i>
                        Details
                    </button>
                </div>
            </div>
        `;
        return card;
    }

    // Generate star rating HTML
    function generateStars(rating) {
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 !== 0;
        const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
        
        let starsHTML = '';
        
        // Full stars
        for (let i = 0; i < fullStars; i++) {
            starsHTML += '<i class="fas fa-star"></i>';
        }
        
        // Half star
        if (hasHalfStar) {
            starsHTML += '<i class="fas fa-star-half-alt"></i>';
        }
        
        // Empty stars
        for (let i = 0; i < emptyStars; i++) {
            starsHTML += '<i class="far fa-star"></i>';
        }
        
        return starsHTML;
    }

    // Setup event listeners
    function setupEventListeners() {
        // Load more button
        loadMoreBtn.addEventListener('click', function() {
            if (!isLoading) {
                isLoading = true;
                this.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Loading...';
                
                setTimeout(() => {
                    loadVehicles();
                    this.innerHTML = '<i class="fas fa-plus"></i> Load More Vehicles';
                    isLoading = false;
                }, 1000);
            }
        });

        // Booking form submission
        bookingForm.addEventListener('submit', async function(e) {
            e.preventDefault();

            // Validate form before submission
            if (!validateBookingForm()) {
                showErrorNotification('Please correct the errors in the form before submitting.');
                return;
            }

            // Show loading state
            const submitBtn = this.querySelector('button[type="submit"]');
            const originalText = submitBtn.innerHTML;
            submitBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Submitting...';
            submitBtn.disabled = true;

            const formData = {
                fullName: document.getElementById('fullName').value.trim(),
                email: document.getElementById('email').value.trim(),
                phone: formatPhoneForStorage(document.getElementById('phone').value),
                vehicleType: document.getElementById('vehicleType').value,
                pickupDate: document.getElementById('pickupDate').value,
                pickupTime: document.getElementById('pickupTime').value,
                pickupLocation: document.getElementById('pickupLocation').value.trim(),
                returnLocation: document.getElementById('returnLocation').value.trim(),
                additionalDetails: document.getElementById('additionalDetails').value.trim(),
            };

            try {
                const result = await submitBookingForm(formData);
                
                if (result.success) {
                    // Check if this is a mock response
                    if (result.warning || result.dev_note?.includes('MOCK')) {
                        showMockWarningNotification(`${result.message}<br><small>${result.warning || 'Use "npm run dev:vercel" for real Google Sheets integration'}</small>`);
                    } else {
                        showSuccessNotification('Your booking request has been submitted successfully! We will contact you soon to confirm details.');
                    }
                    clearFormFields(bookingForm);
                } else {
                    showErrorNotification(result.message || 'Failed to submit booking. Please try again.');
                }
            } catch (error) {
                console.error('Booking submission error:', error);
                showErrorNotification('Failed to submit booking. Please check your connection and try again.');
            } finally {
                // Restore button state
                submitBtn.innerHTML = originalText;
                submitBtn.disabled = false;
            }
        });

        // Form validation on input
        const formInputs = bookingForm.querySelectorAll('input, select, textarea');
        formInputs.forEach(input => {
            input.addEventListener('blur', function() {
                if (this.value.trim() || this.hasAttribute('required')) {
                    validateBookingField(this);
                }
            });
            
            input.addEventListener('input', function() {
                // Clear error on input if field was previously invalid
                if (this.classList.contains('error')) {
                    clearBookingFieldError(this);
                }
            });
        });

        // Enhanced phone input for international support
        const phoneInput = document.getElementById('phone');
        if (phoneInput) {
            phoneInput.addEventListener('input', function(e) {
                const input = e.target;
                const value = input.value;
                
                // Allow international characters: +, spaces, hyphens, parentheses, numbers
                const cleanValue = value.replace(/[^\+\d\s\-\(\)\.]/g, '');
                
                // Update input if cleaned
                if (cleanValue !== value) {
                    input.value = cleanValue;
                }
                
                // Real-time validation feedback
                if (cleanValue.length > 0) {
                    if (isValidPhone(cleanValue)) {
                        showBookingFieldSuccess(input);
                    } else if (cleanValue.length > 5) { // Only show error after reasonable length
                        showBookingFieldError(input, 'Please enter a valid phone number (international format supported).');
                    }
                }
            });
        }
    }

    // Select vehicle function - scrolls to booking section
    window.selectVehicle = function(vehicleId) {
        selectedVehicle = vehicleData.find(v => v.id === vehicleId);
        if (selectedVehicle) {
            // Pre-fill the vehicle type in the form based on selected vehicle
            preSelectVehicleType(selectedVehicle);
            scrollToBookingForm();
        }
    };

    // Pre-select vehicle type based on selected vehicle
    function preSelectVehicleType(vehicle) {
        const vehicleTypeSelect = document.getElementById('vehicleType');
        const passengers = parseInt(vehicle.features[0].split(' ')[0]);
        
        if (passengers <= 5) {
            vehicleTypeSelect.value = 'sedan';
        } else if (passengers <= 7) {
            vehicleTypeSelect.value = 'suv';
        } else if (passengers <= 15) {
            vehicleTypeSelect.value = 'van';
        } else {
            vehicleTypeSelect.value = 'bus';
        }
        
        // Trigger validation for the pre-selected field
        validateBookingField(vehicleTypeSelect);
    }

    // Show vehicle details
    window.showVehicleDetails = function(vehicleId) {
        const vehicle = vehicleData.find(v => v.id === vehicleId);
        if (vehicle) {
            showVehicleDetailsModal(vehicle);
        }
    };

    // Show vehicle details modal
    function showVehicleDetailsModal(vehicle) {
        const modal = document.getElementById('vehicleDetailsModal');
        const title = document.getElementById('vehicleDetailsTitle');
        const content = document.getElementById('vehicleDetailsContent');
        
        title.textContent = vehicle.name;
        
        // Create route pricing table
        const routeNames = {
            'jeddah_to_makkah': 'Jeddah to Makkah',
            'makkah_to_madinah': 'Makkah to Madinah',
            'jeddah_airport_to_madinah': 'Jeddah Airport to Madinah',
            'makkah_to_jeddah': 'Makkah to Jeddah',
            'mazarat_makkah': 'Mazarat Makkah',
            'madinah_apt_madinah_htl': 'Madinah Airport to Madinah Hotel',
            'jeddah_airport_jedda_city': 'Jeddah Airport to Jeddah City',
            'makkah_taif_ziarat': 'Makkah Taif Ziarat',
            'mak_htl_to_mak_trn': 'Makkah Hotel to Makkah Train',
            'mad_htl_to_mad_trn': 'Madinah Hotel to Madinah Train'
        };
        
        content.innerHTML = `
            <div class="vehicle-details-info">
                <div class="vehicle-detail-image">
                    <img src="${vehicle.image}" alt="${vehicle.name}" 
                         onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
                    <div class="vehicle-detail-icon-fallback" style="display: none;">
                        <i class="${getVehicleIcon(vehicle.type)}"></i>
                    </div>
                </div>
                <div class="vehicle-detail-specs">
                    <div class="spec-item">
                        <h4>Vehicle Type</h4>
                        <p>${vehicle.type}</p>
                    </div>
                    <div class="spec-item">
                        <h4>Rating</h4>
                        <div class="rating-display">
                            <div class="stars">${generateStars(vehicle.rating)}</div>
                            <span>${vehicle.rating}/5</span>
                        </div>
                    </div>
                    <div class="spec-item">
                        <h4>Features</h4>
                        <div class="features-list">
                            ${vehicle.features.map(feature => `<span class="feature-badge">${feature}</span>`).join('')}
                        </div>
                    </div>
                    <div class="spec-item">
                        <h4>Description</h4>
                        <p>${vehicle.description}</p>
                    </div>
                </div>
            </div>
            
            <div class="route-pricing">
                <h3>Route Pricing</h3>
                <div class="pricing-table">
                    <div class="pricing-header">
                        <div class="route-col">Route</div>
                        <div class="price-col">Price</div>
                    </div>
                    <div class="pricing-body">
                        ${Object.entries(vehicle.routes).map(([routeKey, price]) => `
                            <div class="pricing-row">
                                <div class="route-col">${routeNames[routeKey] || routeKey}</div>
                                <div class="price-col">${price}</div>
                            </div>
                        `).join('')}
                    </div>
                </div>
            </div>
            
            <div class="modal-actions">
                <button class="btn-primary" onclick="selectVehicle(${vehicle.id}); closeVehicleDetailsModal();">
                    <i class="fas fa-calendar-check"></i> Book This Vehicle
                </button>
                <button class="btn-secondary" onclick="closeVehicleDetailsModal()">
                    <i class="fas fa-times"></i> Close
                </button>
            </div>
        `;
        
        modal.style.display = 'flex';
        document.body.style.overflow = 'hidden';
    }

    // Close vehicle details modal
    window.closeVehicleDetailsModal = function() {
        const modal = document.getElementById('vehicleDetailsModal');
        modal.style.display = 'none';
        document.body.style.overflow = '';
    };

    // Scroll to booking form
    function scrollToBookingForm() {
        setTimeout(() => {
            newBookingSection.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
            });
        }, 300);
    }

    // Set minimum date to today
    function setMinDate() {
        const today = new Date().toISOString().split('T')[0];
        const pickupDateInput = document.getElementById('pickupDate');
        if (pickupDateInput) {
            pickupDateInput.min = today;
        }
    }

    // Populate location dropdowns
    function populateLocationDropdowns() {
        const pickupSelect = document.getElementById('pickupLocation');
        const returnSelect = document.getElementById('returnLocation');
        
        if (!pickupSelect || !returnSelect) return;
        
        // Extract unique locations from all vehicle routes
        const locations = new Set();
        
        const routeNames = {
            'jeddah_to_makkah': ['Jeddah', 'Makkah'],
            'makkah_to_madinah': ['Makkah', 'Madinah'],
            'jeddah_airport_to_madinah': ['Jeddah Airport', 'Madinah'],
            'makkah_to_jeddah': ['Makkah', 'Jeddah'],
            'mazarat_makkah': ['Mazarat', 'Makkah'],
            'madinah_apt_madinah_htl': ['Madinah Airport', 'Madinah Hotel'],
            'jeddah_airport_jedda_city': ['Jeddah Airport', 'Jeddah City'],
            'makkah_taif_ziarat': ['Makkah', 'Taif'],
            'mak_htl_to_mak_trn': ['Makkah Hotel', 'Makkah Train Station'],
            'mad_htl_to_mad_trn': ['Madinah Hotel', 'Madinah Train Station']
        };
        
        // Add all unique locations to the set
        Object.values(routeNames).forEach(routeLocations => {
            routeLocations.forEach(location => locations.add(location));
        });
        
        // Sort locations alphabetically
        const sortedLocations = Array.from(locations).sort();
        
        // Populate both dropdowns
        sortedLocations.forEach(location => {
            const pickupOption = document.createElement('option');
            pickupOption.value = location;
            pickupOption.textContent = location;
            pickupSelect.appendChild(pickupOption);
            
            const returnOption = document.createElement('option');
            returnOption.value = location;
            returnOption.textContent = location;
            returnSelect.appendChild(returnOption);
        });
    }

    // Validate the entire booking form
    function validateBookingForm() {
        const form = document.getElementById('bookingForm');
        const inputs = form.querySelectorAll('input[required], select[required], textarea[required]');
        let isValid = true;

        inputs.forEach(input => {
            if (!validateBookingField(input)) {
                isValid = false;
            }
        });

        return isValid;
    }

    // Enhanced booking field validation
    function validateBookingField(field) {
        const value = field.value.trim();
        const fieldName = field.getAttribute('name') || field.id;
        let isValid = true;
        let errorMessage = '';

        // Clear previous error
        clearBookingFieldError(field);

        // Required field validation
        if (field.hasAttribute('required') && !value) {
            errorMessage = `${getBookingFieldLabel(fieldName)} is required.`;
            isValid = false;
        }
        // Email validation
        else if (fieldName === 'email' && value && !isValidEmail(value)) {
            errorMessage = 'Please enter a valid email address.';
            isValid = false;
        }
        // Phone validation
        else if (fieldName === 'phone' && value && !isValidPhone(value)) {
            errorMessage = 'Please enter a valid phone number (international format supported).';
            isValid = false;
        }
        // Full name validation (at least 2 characters)
        else if (fieldName === 'fullName' && value && value.length < 2) {
            errorMessage = 'Full name must be at least 2 characters long.';
            isValid = false;
        }
        // Date validation (not in the past)
        else if (fieldName === 'pickupDate' && value) {
            const selectedDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (selectedDate < today) {
                errorMessage = 'Pickup date cannot be in the past.';
                isValid = false;
            }
        }
        // Location validation (at least 3 characters)
        else if ((fieldName === 'pickupLocation' || fieldName === 'returnLocation') && value && value.length < 3) {
            errorMessage = `${getBookingFieldLabel(fieldName)} must be at least 3 characters long.`;
            isValid = false;
        }

        if (!isValid) {
            showBookingFieldError(field, errorMessage);
        } else {
            showBookingFieldSuccess(field);
        }

        return isValid;
    }

    // Get user-friendly field label for booking form
    function getBookingFieldLabel(fieldName) {
        const labels = {
            'fullName': 'Full Name',
            'email': 'Email',
            'phone': 'Phone Number',
            'vehicleType': 'Vehicle Type',
            'pickupDate': 'Pickup Date',
            'pickupTime': 'Pickup Time',
            'pickupLocation': 'Pickup Location',
            'returnLocation': 'Return Location',
            'additionalDetails': 'Additional Details'
        };
        return labels[fieldName] || fieldName;
    }

    // Show booking field error
    function showBookingFieldError(field, message) {
        field.style.borderColor = '#dc3545';
        field.classList.add('error');
        field.classList.remove('success');
        
        const formGroup = field.parentElement;
        const errorElement = document.createElement('div');
        errorElement.className = 'error-message';
        errorElement.style.cssText = `
            color: #dc3545;
            font-size: 12px;
            margin-top: 5px;
            display: flex;
            align-items: center;
            gap: 5px;
        `;
        errorElement.innerHTML = `<i class="fas fa-exclamation-circle"></i>${message}`;
        formGroup.appendChild(errorElement);
    }

    // Show booking field success
    function showBookingFieldSuccess(field) {
        field.style.borderColor = '#28a745';
        field.classList.add('success');
        field.classList.remove('error');
    }

    // Clear booking field error
    function clearBookingFieldError(field) {
        field.style.borderColor = '#e9ecef';
        field.classList.remove('error', 'success');
        
        const formGroup = field.parentElement;
        const errorMessage = formGroup.querySelector('.error-message');
        if (errorMessage) {
            errorMessage.remove();
        }
    }

    // Submit booking
    function submitBooking() {
        showLoading();
        
        // Simulate API call
        setTimeout(() => {
            hideLoading();
            showSuccessModal();
            resetForm();
        }, 2000);
    }

    // Show/hide loading
    function showLoading() {
        loadingOverlay.classList.add('active');
    }

    function hideLoading() {
        loadingOverlay.classList.remove('active');
    }

    // Show success modal
    function showSuccessModal() {
        successModal.classList.add('active');
    }

    // Close modal and redirect
    window.closeModal = function() {
        successModal.classList.remove('active');
        // You can redirect to home or any other page
        // window.location.href = '/';
    };

    // Reset form
    function resetForm() {
        bookingForm.reset();
        
        // Clear all validation states
        const formGroups = bookingForm.querySelectorAll('.form-group');
        formGroups.forEach(group => {
            group.classList.remove('error', 'success');
            const errorMessage = group.querySelector('.error-message');
            if (errorMessage) {
                errorMessage.remove();
            }
        });
    }

    // Redirect to contact page function (for consultant button)
    window.redirectToContact = function() {
        // You can change this URL to your actual contact page
        window.location.href = '/contact.html';
        // Or open in new tab: window.open('/contact.html', '_blank');
    };

    // Initialize the page when DOM is loaded
    init();
});